/**
 * Purchase Order Status Screen (4.3.6)
 * Procurement Module - Orders
 * UI Layout: EXACT MATCH to PDF specification
 * 
 * Layout: PO status tracking with GRN history
 */

import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertCircle, Search, X, FileText, Package, CheckCircle, Clock, XCircle } from 'lucide-react';

const PurchaseOrderStatusScreen = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  
  // Filter state
  const [filters, setFilters] = useState({
    fromDate: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0],
    supplier: '',
    status: 'All',
  });

  const [data, setData] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [poDetails, setPoDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Theme colors
  const bgColor = isDark ? 'bg-[#1a1a2e]' : 'bg-gray-100';
  const cardBg = isDark ? 'bg-[#16213e]' : 'bg-white';
  const inputBg = isDark ? 'bg-[#0f0f1a]' : 'bg-gray-50';
  const borderColor = isDark ? 'border-[#1f4068]' : 'border-gray-300';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const labelColor = isDark ? 'text-gray-300' : 'text-gray-700';
  const sectionBg = isDark ? 'bg-[#1f2937]' : 'bg-gray-50';

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      setData([
        { poNo: 'PO101L0001', poDate: '2024-01-15', supplier: 'FASHION FOREC', items: 5, poQty: '150.000', receivedQty: '150.000', pendingQty: '0.000', status: 'Completed' },
        { poNo: 'PO101L0002', poDate: '2024-01-16', supplier: 'BALAJI ENTERPRISES', items: 3, poQty: '80.000', receivedQty: '50.000', pendingQty: '30.000', status: 'Partial' },
        { poNo: 'PO101L0003', poDate: '2024-01-18', supplier: 'KRISHNA TEXTILES', items: 8, poQty: '200.000', receivedQty: '0.000', pendingQty: '200.000', status: 'Open' },
        { poNo: 'PO101L0004', poDate: '2024-01-10', supplier: 'SAI FABRICS', items: 2, poQty: '50.000', receivedQty: '0.000', pendingQty: '0.000', status: 'Cancelled' },
      ]);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleViewPODetails = (po) => {
    setSelectedPO(po);
    setPoDetails({
      po: po,
      items: [
        { itemCode: 'IT10001', itemName: 'MENS FORMAL SHIRT', poQty: '100.000', receivedQty: po.status === 'Completed' ? '100.000' : '70.000', pendingQty: po.status === 'Completed' ? '0.000' : '30.000' },
        { itemCode: 'IT10002', itemName: 'LADIES TOP', poQty: '50.000', receivedQty: po.status === 'Completed' ? '50.000' : '30.000', pendingQty: po.status === 'Completed' ? '0.000' : '20.000' },
      ],
      grns: po.status !== 'Open' ? [
        { grnNo: 'GRN101L0001', grnDate: '2024-01-20', qty: '70.000', value: '105000.00' },
        { grnNo: 'GRN101L0002', grnDate: '2024-01-25', qty: '30.000', value: '45000.00' },
      ] : [],
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed': return <CheckCircle size={14} className="text-emerald-400" />;
      case 'Partial': return <Clock size={14} className="text-orange-400" />;
      case 'Open': return <Package size={14} className="text-blue-400" />;
      case 'Cancelled': return <XCircle size={14} className="text-red-400" />;
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-emerald-500/20 text-emerald-400';
      case 'Partial': return 'bg-orange-500/20 text-orange-400';
      case 'Open': return 'bg-blue-500/20 text-blue-400';
      case 'Cancelled': return 'bg-red-500/20 text-red-400';
      default: return '';
    }
  };

  // Input field component
  const InputField = ({ label, name, value, onChange, type = 'text' }) => (
    <div className="flex items-center">
      <label className={`w-20 text-xs ${labelColor}`}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        className={`flex-1 px-2 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`}
        data-testid={`input-${name}`}
      />
    </div>
  );

  const SelectField = ({ label, name, value, onChange, options }) => (
    <div className="flex items-center">
      <label className={`w-20 text-xs ${labelColor}`}>{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        className={`flex-1 px-2 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`}
        data-testid={`select-${name}`}
      >
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className={`min-h-screen ${bgColor} ${textColor} p-4`} data-testid="purchase-order-status-screen">
      {/* Header */}
      <div className={`${cardBg} rounded-lg p-3 mb-4 border ${borderColor}`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">Purchase Order Status</h1>
            <p className="text-xs text-gray-400">Screen ID: 4.3.6 | Procurement Module</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadData} className="p-2 rounded bg-blue-600 hover:bg-blue-700 text-white" data-testid="refresh-btn">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2">
          <AlertCircle className="text-red-400" size={18} />
          <span className="text-red-400 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">×</button>
        </div>
      )}

      {/* Filters */}
      <div className={`${cardBg} rounded-lg border ${borderColor} p-4 mb-4`}>
        <div className={`${sectionBg} rounded-lg p-3 border ${borderColor}`}>
          <div className="grid grid-cols-4 gap-4">
            <InputField label="From Date" name="fromDate" value={filters.fromDate} onChange={(n, v) => setFilters(p => ({ ...p, [n]: v }))} type="date" />
            <InputField label="To Date" name="toDate" value={filters.toDate} onChange={(n, v) => setFilters(p => ({ ...p, [n]: v }))} type="date" />
            <div className="flex items-center">
              <label className={`w-20 text-xs ${labelColor}`}>Supplier</label>
              <div className="flex-1 flex gap-1">
                <input
                  type="text"
                  value={filters.supplier}
                  onChange={(e) => setFilters(p => ({ ...p, supplier: e.target.value }))}
                  className={`flex-1 px-2 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`}
                  placeholder="All Suppliers"
                />
                <button className={`p-1 rounded border ${borderColor} ${inputBg}`}>
                  <Search size={12} className="text-gray-400" />
                </button>
              </div>
            </div>
            <SelectField label="Status" name="status" value={filters.status} onChange={(n, v) => setFilters(p => ({ ...p, [n]: v }))} options={['All', 'Open', 'Partial', 'Completed', 'Cancelled']} />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        {['Open', 'Partial', 'Completed', 'Cancelled'].map(status => {
          const count = data.filter(d => d.status === status).length;
          return (
            <div key={status} className={`${cardBg} rounded-lg p-3 border ${borderColor}`}>
              <div className="flex items-center gap-2">
                {getStatusIcon(status)}
                <p className={`text-xs ${labelColor}`}>{status}</p>
              </div>
              <p className="text-2xl font-bold mt-1">{count}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* PO List */}
        <div className={`${cardBg} rounded-lg border ${borderColor} overflow-hidden`}>
          <div className={`p-3 border-b ${borderColor}`}>
            <h3 className="text-sm font-semibold">Purchase Orders</h3>
          </div>
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-xs">
              <thead className={isDark ? 'bg-[#0f0f1a]' : 'bg-gray-100'}>
                <tr>
                  <th className="px-2 py-2 text-left">PO No</th>
                  <th className="px-2 py-2 text-left">Date</th>
                  <th className="px-2 py-2 text-left">Supplier</th>
                  <th className="px-2 py-2 text-right">PO Qty</th>
                  <th className="px-2 py-2 text-right">Received</th>
                  <th className="px-2 py-2 text-right">Pending</th>
                  <th className="px-2 py-2 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.map(po => (
                  <tr 
                    key={po.poNo} 
                    className={`border-t ${borderColor} cursor-pointer hover:${isDark ? 'bg-[#0f0f1a]' : 'bg-gray-50'} ${selectedPO?.poNo === po.poNo ? (isDark ? 'bg-blue-900/30' : 'bg-blue-100') : ''}`}
                    onClick={() => handleViewPODetails(po)}
                  >
                    <td className="px-2 py-2 font-mono text-blue-400">{po.poNo}</td>
                    <td className="px-2 py-2">{po.poDate}</td>
                    <td className="px-2 py-2">{po.supplier}</td>
                    <td className="px-2 py-2 text-right">{po.poQty}</td>
                    <td className="px-2 py-2 text-right text-emerald-400">{po.receivedQty}</td>
                    <td className="px-2 py-2 text-right text-orange-400">{po.pendingQty}</td>
                    <td className="px-2 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(po.status)}`}>
                        {po.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* PO Details */}
        <div className={`${cardBg} rounded-lg border ${borderColor} overflow-hidden`}>
          <div className={`p-3 border-b ${borderColor}`}>
            <h3 className="text-sm font-semibold">PO Details {selectedPO && `- ${selectedPO.poNo}`}</h3>
          </div>
          {poDetails ? (
            <div className="p-3 space-y-4 max-h-96 overflow-y-auto">
              {/* Items */}
              <div>
                <h4 className={`text-xs font-semibold mb-2 ${labelColor}`}>Items</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className={isDark ? 'bg-[#0f0f1a]' : 'bg-gray-100'}>
                      <tr>
                        <th className="px-2 py-1 text-left">Item Code</th>
                        <th className="px-2 py-1 text-left">Item Name</th>
                        <th className="px-2 py-1 text-right">PO Qty</th>
                        <th className="px-2 py-1 text-right">Received</th>
                        <th className="px-2 py-1 text-right">Pending</th>
                      </tr>
                    </thead>
                    <tbody>
                      {poDetails.items.map((item, idx) => (
                        <tr key={idx} className={`border-t ${borderColor}`}>
                          <td className="px-2 py-1 font-mono">{item.itemCode}</td>
                          <td className="px-2 py-1">{item.itemName}</td>
                          <td className="px-2 py-1 text-right">{item.poQty}</td>
                          <td className="px-2 py-1 text-right text-emerald-400">{item.receivedQty}</td>
                          <td className="px-2 py-1 text-right text-orange-400">{item.pendingQty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* GRN History */}
              {poDetails.grns.length > 0 && (
                <div>
                  <h4 className={`text-xs font-semibold mb-2 ${labelColor}`}>GRN History</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className={isDark ? 'bg-[#0f0f1a]' : 'bg-gray-100'}>
                        <tr>
                          <th className="px-2 py-1 text-left">GRN No</th>
                          <th className="px-2 py-1 text-left">GRN Date</th>
                          <th className="px-2 py-1 text-right">Qty</th>
                          <th className="px-2 py-1 text-right">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {poDetails.grns.map((grn, idx) => (
                          <tr key={idx} className={`border-t ${borderColor}`}>
                            <td className="px-2 py-1 font-mono text-blue-400">{grn.grnNo}</td>
                            <td className="px-2 py-1">{grn.grnDate}</td>
                            <td className="px-2 py-1 text-right">{grn.qty}</td>
                            <td className="px-2 py-1 text-right">{grn.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {poDetails.grns.length === 0 && (
                <div className={`text-center py-4 ${labelColor}`}>
                  <Package size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-xs">No GRNs received yet</p>
                </div>
              )}
            </div>
          ) : (
            <div className={`p-8 text-center ${labelColor}`}>
              <FileText size={48} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Select a PO to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderStatusScreen;
